import { CatchError } from 'Exceptions/exceptions'
import SecurityService from 'Main/service/SecurityService'
import { DEFAULT_FILTERS } from 'Main/Utils'
import { WINDOW_OPTIONS } from 'Main/Utils'
import { humanize } from 'Main/Utils'
import { SeverityBadge } from 'Main/Utils'
import { SEVERITY_OPTIONS } from 'Main/Utils'
import { formatTimestamp } from 'Main/Utils'
import { emptyOverview } from 'Main/Utils'
import { pageSizeConfigs } from 'Main/Utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DatalogPagination from 'views/components/Datalog/DatalogPagination'
import CommonSelect from 'views/components/Input/CommonSelect'
import SecurityEventDetailModal from './SecurityEventDetailModal'

function SecurityManager () {
  const navigate = useNavigate()
  const securityService = useMemo(() => new SecurityService(), [])
  const [overview, setOverview] = useState(emptyOverview)
  const [events, setEvents] = useState([])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [hours, setHours] = useState(24)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPage, setTotalPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [eventsLoading, setEventsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [detailEventId, setDetailEventId] = useState(null)
  const [detailEvent, setDetailEvent] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const filtersRef = useRef(DEFAULT_FILTERS)
  const hoursRef = useRef(24)
  const pageRef = useRef(1)
  const detailRequestRef = useRef(0)

  const loadOverview = useCallback(async (activeHours = 24, quiet = false) => {
    try {
      if (!quiet) setLoading(true)
      const response = await securityService.getOverview(activeHours)
      setOverview(response?.data || emptyOverview)
      setError(null)
    } catch (requestError) {
      CatchError(requestError, navigate)
      if (!quiet) setError('Security Eventis temporarily unavailable.')
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [navigate, securityService])

  const loadEvents = useCallback(async (page = 1, activeFilters = DEFAULT_FILTERS, quiet = false) => {
    try {
      if (!quiet) setEventsLoading(true)
      const response = await securityService.getEvents({
        page,
        size: activeFilters.pageSize,
        severity: activeFilters.severity,
        attackType: activeFilters.attackType,
        sourceIp: activeFilters.sourceIp.trim(),
        from: activeFilters.from ? new Date(`${activeFilters.from}T00:00:00`).toISOString() : '',
        to: activeFilters.to ? new Date(`${activeFilters.to}T23:59:59.999`).toISOString() : ''
      })
      const data = response?.data || {}
      setEvents(data.result || [])
      setTotalRecords(Number(data.max || 0))
      setTotalPage(Number(data.totalPage || 1))
      setError(null)
    } catch (requestError) {
      CatchError(requestError, navigate)
      if (!quiet) setError('Blocked Request records could not be loaded.')
    } finally {
      if (!quiet) setEventsLoading(false)
    }
  }, [navigate, securityService])

  const refreshAll = useCallback((quiet = false) => {
    void loadOverview(hoursRef.current, quiet)
    void loadEvents(pageRef.current, filtersRef.current, quiet)
  }, [loadEvents, loadOverview])

  useEffect(() => {
    refreshAll(false)
    const interval = window.setInterval(() => refreshAll(true), 30000)
    return () => window.clearInterval(interval)
  }, [refreshAll])

  const changeWindow = (nextHours) => {
    hoursRef.current = nextHours
    setHours(nextHours)
    loadOverview(nextHours)
  }

  const changeFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }))
  }

  const filterBySource = (sourceIp) => {
    const nextFilters = { ...filtersRef.current, sourceIp }
    filtersRef.current = nextFilters
    pageRef.current = 1
    setFilters(nextFilters)
    setCurrentPage(1)
    loadEvents(1, nextFilters)
  }

  const applyFilters = (event) => {
    event?.preventDefault()
    filtersRef.current = filters
    pageRef.current = 1
    setCurrentPage(1)
    loadEvents(1, filters)
  }

  const clearFilters = () => {
    filtersRef.current = DEFAULT_FILTERS
    pageRef.current = 1
    setFilters(DEFAULT_FILTERS)
    setCurrentPage(1)
    loadEvents(1, DEFAULT_FILTERS)
  }

  const changePage = (page) => {
    pageRef.current = page
    setCurrentPage(page)
    loadEvents(page, filtersRef.current)
  }

  const loadEventDetail = useCallback(async (id) => {
    const requestId = detailRequestRef.current + 1
    detailRequestRef.current = requestId
    setDetailLoading(true)
    setDetailError(null)

    try {
      const response = await securityService.getEvent(id)
      if (detailRequestRef.current !== requestId) return
      if (!response?.data) throw new Error('Security Eventevent detail is unavailable.')
      setDetailEvent(response.data)
    } catch (requestError) {
      if (detailRequestRef.current !== requestId) return
      CatchError(requestError, navigate)
      setDetailError(requestError?.response?.data?.message || requestError.message || 'Security Eventevent detail could not be loaded.')
    } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false)
    }
  }, [navigate, securityService])

  const openEventDetail = (id) => {
    setDetailEventId(id)
    setDetailEvent(null)
    void loadEventDetail(id)
  }

  const closeEventDetail = useCallback(() => {
    detailRequestRef.current += 1
    setDetailEventId(null)
    setDetailEvent(null)
    setDetailLoading(false)
    setDetailError(null)
  }, [])

  const counters = overview.counters || emptyOverview.counters
  const hasCritical = Number(counters.critical) > 0
  const hasAttacks = Number(counters.total) > 0
  const alertTone = hasCritical ? 'critical' : hasAttacks ? 'warning' : 'clear'
  const alertTitle = hasCritical
    ? `${counters.critical} critical request${Number(counters.critical) === 1 ? '' : 's'} blocked`
    : hasAttacks
      ? `${counters.total} request${Number(counters.total) === 1 ? '' : 's'} blocked`
      : 'No blocked request in this time'

  return (
    <div className="security-dashboard">
      <section className={`security-alert-banner tone-${alertTone}`} role={hasAttacks ? 'alert' : 'status'}>
        <div className="security-alert-icon">
          <i className={`fa-solid fa-shield`} />
        </div>
        <div className="security-alert-copy">
          <h2>{loading ? 'Checking Security Event…' : alertTitle}</h2>
          <p>
            {hasAttacks
              ? `${overview.hours || hours} jam terakhir`
              : ''}
          </p>
        </div>
        <div className="security-alert-controls">
          <div className="security-window-picker" aria-label="Monitoring window">
            {WINDOW_OPTIONS.map((option) => (
              <button
                key={option.hours}
                type="button"
                className={hours === option.hours ? 'active' : ''}
                onClick={() => changeWindow(option.hours)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button type="button" className="security-refresh" onClick={() => refreshAll(false)} disabled={loading || eventsLoading}>
            <i className={`fa-solid fa-rotate${loading || eventsLoading ? ' fa-spin' : ''}`} /> Refresh
          </button>
        </div>
      </section>

      {error && (
        <div className="gateway-error" role="alert">
          <i className="fa-solid fa-triangle-exclamation" />
          <span>{error}</span>
          <button type="button" onClick={() => refreshAll(false)}>Try again</button>
        </div>
      )}

      <section className="security-stat-grid" aria-label="Security Eventattempt summary">
        <article className="security-stat-card tone-danger">
          <span><i className="fa-solid fa-ban" /> Blocked</span>
          <strong>{Number(counters.total || 0).toLocaleString()}</strong>
        </article>
        <article className="security-stat-card tone-critical">
          <span><i className="fa-solid fa-radiation" /> Critical</span>
          <strong>{Number(counters.critical || 0).toLocaleString()}</strong>
        </article>
        <article className="security-stat-card tone-warning">
          <span><i className="fa-solid fa-network-wired" /> sources</span>
          <strong>{Number(counters.uniqueSources || 0).toLocaleString()}</strong>
        </article>
        <article className="security-stat-card tone-info">
          <span><i className="fa-solid fa-clock-rotate-left" /> Latest Blocked</span>
          <strong className="security-stat-time">{formatTimestamp(overview.latest?.creation)}</strong>
          <small>{overview.latest ? humanize(overview.latest.attack_type) : 'No activity in this window'}</small>
        </article>
      </section>

      <section className="security-panel security-events-panel">
        <div className="security-panel-heading security-events-heading">
          <div>
            <h3>Blocked Request</h3>
          </div>
          <span className="security-count-pill">{totalRecords.toLocaleString()} recorded</span>
        </div>

        <form className="security-filters" onSubmit={applyFilters}>
          <label>
            <CommonSelect
              label = {"Severity"}
              value={filters.severity}
              disabled = {false}
              options = {SEVERITY_OPTIONS}
              onChange = {(event) => changeFilter('severity', event.target.value)}
            />
          </label>
          <label>
            <CommonSelect
              label = {"Attack type"}
              value={filters.severity}
              disabled = {false}
              options = {overview.attackTypes}
              onChange = {(event) => changeFilter('attackType', event.target.value)}
            />
            <span></span>
          </label>
          <label>
            <span>Source IP</span>
            <input
              type="text"
              value={filters.sourceIp}
              maxLength={64}
              placeholder="10.2.2.1"
              onChange={(event) => changeFilter('sourceIp', event.target.value)}
            />
          </label>
          <label>
            <span>From</span>
            <input type="date" value={filters.from} onChange={(event) => changeFilter('from', event.target.value)} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={filters.to} onChange={(event) => changeFilter('to', event.target.value)} />
          </label>
          <label>
            <span>Rows</span>
            <select value={filters.pageSize} onChange={(event) => changeFilter('pageSize', Number(event.target.value))}>
              {pageSizeConfigs.filter((size) => size <= 100).map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
          <div className="security-filter-actions">
            <button className="btn btn-primary" type="submit"><i className="fa-solid fa-filter me-1" /> Apply</button>
            <button className="btn btn-secondary" type="button" onClick={clearFilters}>Clear</button>
          </div>
        </form>

        <div className="security-table-wrap">
          <table className="security-events-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Time</th>
                <th>Severity</th>
                <th>Attack</th>
                <th>Source IP</th>
                <th>Request target</th>
                <th>Status Code</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {eventsLoading && (
                <tr><td colSpan="8" className="security-table-state"><i className="fa-solid fa-circle-notch fa-spin" /> Loading request…</td></tr>
              )}
              {!eventsLoading && events.map((item, i) => (
                <tr className={`attempt-${String(item.severity || 'low').toLowerCase()}`} key={item.id}>
                  <td>{i + 1}</td>
                  <td className="security-time-cell">{formatTimestamp(item.creation)}</td>
                  <td><SeverityBadge severity={item.severity} /></td>
                  <td>
                    <strong>{humanize(item.attack_type)}</strong>
                    <small>{humanize(item.category)}</small>
                  </td>
                  <td><code>{item.source_ip}</code></td>
                  <td className="security-target-cell">
                    <span>{item.method}</span>
                    <code title={item.endpoint}>{item.endpoint}</code>
                  </td>
                  <td><span className="security-blocked"> {item.status_code}</span></td>
                  <td>
                    <button
                      type="button"
                      className="security-detail-button"
                      title="View blocked request details"
                      aria-label={`View details for Security Eventevent ${item.id}`}
                      onClick={() => openEventDetail(item.id)}
                    >
                      <i className="fa-solid fa-eye" />
                      <span>View</span>
                    </button>
                  </td>
                </tr>
              ))}
              {!eventsLoading && events.length === 0 && (
                <tr><td colSpan="8" className="security-table-state"> No request match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="security-pagination">
          <DatalogPagination
            currentPage={currentPage}
            totalPage={totalPage}
            totalRecords={totalRecords}
            pageSize={Number(filtersRef.current.pageSize)}
            loading={eventsLoading}
            onPageChange={changePage}
          />
        </div>
      </section>

      <SecurityEventDetailModal
        show={detailEventId !== null}
        eventId={detailEventId}
        attempt={detailEvent}
        loading={detailLoading}
        error={detailError}
        onClose={closeEventDetail}
        onRetry={() => loadEventDetail(detailEventId)}
      />
    </div>
  )
}

export default SecurityManager
